import React, { useState } from 'react';
import AdaptiveProfitChart, { ChartMetric } from './AdaptiveProfitChart';
import { ChartDataPoint, AggregatedData } from '../../utils/dataAggregation';
import { ChartType, ChartGranularity } from '../../utils/chartUtils';
import './AdaptiveProfitChartSection.css';

/**
 * Props for the AdaptiveProfitChartSection component
 */
export interface AdaptiveProfitChartSectionProps {
  /** Section title */
  title: string;
  /** Chart data points */
  data: (ChartDataPoint | AggregatedData)[];
  /** Chart granularity */
  granularity: ChartGranularity;
  /** Chart type */
  chartType: ChartType;
  /** Click handler for data points */
  onDataPointClick?: (dataPoint: ChartDataPoint | AggregatedData) => void;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Empty state message */
  emptyMessage?: string;
}

/**
 * AdaptiveProfitChartSection Component
 * 
 * Complete chart section with controls (metric toggle, cumulative toggle)
 * and the adaptive chart component.
 */
const AdaptiveProfitChartSection: React.FC<AdaptiveProfitChartSectionProps> = ({
  title,
  data,
  granularity,
  chartType,
  onDataPointClick,
  loading = false,
  error = null,
  emptyMessage
}) => {
  const [metric, setMetric] = useState<ChartMetric>('net_profit');
  const [showCumulative, setShowCumulative] = useState(false);

  return (
    <div className="adaptive-chart-section">
      {/* Section Header with Title */}
      <div className="chart-section-header">
        <h3 className="chart-section-title">{title}</h3>
      </div>

      {/* Chart Controls */}
      <div className="chart-controls">
        {/* Metric Toggle */}
        <div className="metric-toggle">
          <button
            className={`metric-button ${metric === 'net_profit' ? 'active' : ''}`}
            onClick={() => setMetric('net_profit')}
            aria-label="Show net profit"
          >
            Net Profit
          </button>
          <button
            className={`metric-button ${metric === 'gross_profit' ? 'active' : ''}`}
            onClick={() => setMetric('gross_profit')}
            aria-label="Show gross profit"
          >
            Gross Profit
          </button>
          <button
            className={`metric-button ${metric === 'trade_count' ? 'active' : ''}`}
            onClick={() => setMetric('trade_count')}
            aria-label="Show trade count"
          >
            Trade Count
          </button>
        </div>

        {/* Cumulative Toggle */}
        <label className="cumulative-toggle">
          <input
            type="checkbox"
            checked={showCumulative}
            onChange={(e) => setShowCumulative(e.target.checked)}
            aria-label="Show cumulative profit"
          />
          <span className="toggle-label">Show Cumulative</span>
        </label>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <AdaptiveProfitChart
          data={data}
          granularity={granularity}
          chartType={chartType}
          metric={metric}
          showCumulative={showCumulative}
          onDataPointClick={onDataPointClick}
          loading={loading}
          error={error}
          emptyMessage={emptyMessage}
        />
      </div>
    </div>
  );
};

export default AdaptiveProfitChartSection;
